import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import apiClient from '../../api/client';

interface PendingRequest {
  request_id: string;
  username: string;
  email: string;
  company?: string;
  message?: string;
  status: 'pending' | 'approved' | 'rejected';
  submitted_at: string;
  approved_at?: string;
  rejected_at?: string;
  rejection_reason?: string;
}

interface ApprovalData {
  initial_credits: number;
  role: 'user' | 'b2b-user' | 'admin';
}

const PendingRegistrations: React.FC = () => {
  const { t } = useLanguage();
  const [requests, setRequests] = useState<PendingRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<PendingRequest | null>(null);
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [approvalData, setApprovalData] = useState<ApprovalData>({
    initial_credits: 10,
    role: 'b2b-user'
  });
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const loadPendingRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/admin/registrations/pending');
      setRequests(response.data.requests || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load pending registrations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPendingRequests();
  }, []);

  const handleApproveClick = (request: PendingRequest) => {
    setSelectedRequest(request);
    setApprovalData({
      initial_credits: 10,
      role: 'b2b-user'
    });
    setApprovalDialog(true);
  };

  const handleRejectClick = (request: PendingRequest) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setRejectDialog(true);
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;

    try {
      setProcessing(true);
      await apiClient.post(`/admin/registrations/${selectedRequest.request_id}/approve`, approvalData);
      setApprovalDialog(false);
      setSelectedRequest(null);
      loadPendingRequests();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to approve registration');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;

    try {
      setProcessing(true);
      await apiClient.post(`/admin/registrations/${selectedRequest.request_id}/reject`, {
        reason: rejectionReason
      });
      setRejectDialog(false);
      setSelectedRequest(null);
      loadPendingRequests();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to reject registration');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const pendingOnly = requests.filter(r => r.status === 'pending');
  const processedRequests = requests.filter(r => r.status !== 'pending');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Pending Registrations ({pendingOnly.length})
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadPendingRequests}
          variant="outlined"
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Pending Requests */}
      {pendingOnly.length === 0 ? (
        <Alert severity="info">
          No pending registration requests
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Message</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingOnly.map((request) => (
                <TableRow key={request.request_id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {request.username}
                    </Typography>
                  </TableCell>
                  <TableCell>{request.email}</TableCell>
                  <TableCell>{request.company || '-'}</TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {formatDate(request.submitted_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={request.message || 'No message'}>
                      <IconButton size="small">
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<ApproveIcon />}
                      onClick={() => handleApproveClick(request)}
                      color="success"
                      variant="contained"
                      sx={{ mr: 1 }}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      startIcon={<RejectIcon />}
                      onClick={() => handleRejectClick(request)}
                      color="error"
                      variant="outlined"
                    >
                      Reject
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Processed Requests */}
      {processedRequests.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
            Processed Requests ({processedRequests.length})
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Processed At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {processedRequests.map((request) => (
                  <TableRow key={request.request_id}>
                    <TableCell>{request.username}</TableCell>
                    <TableCell>{request.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={request.status}
                        color={getStatusColor(request.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {request.approved_at
                          ? formatDate(request.approved_at)
                          : request.rejected_at
                          ? formatDate(request.rejected_at)
                          : '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Approval Dialog */}
      <Dialog open={approvalDialog} onClose={() => !processing && setApprovalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Registration</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Username:</strong> {selectedRequest.username}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Email:</strong> {selectedRequest.email}
              </Typography>
              {selectedRequest.company && (
                <Typography variant="body2" gutterBottom>
                  <strong>Company:</strong> {selectedRequest.company}
                </Typography>
              )}
              {selectedRequest.message && (
                <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
                  <strong>Message:</strong><br />
                  {selectedRequest.message}
                </Typography>
              )}

              <TextField
                select
                label="Role"
                value={approvalData.role}
                onChange={(e) => setApprovalData({ ...approvalData, role: e.target.value as any })}
                fullWidth
                margin="normal"
              >
                <MenuItem value="user">user</MenuItem>
                <MenuItem value="b2b-user">b2b-user</MenuItem>
                <MenuItem value="admin">admin</MenuItem>
              </TextField>

              <TextField
                type="number"
                label="Initial Credits"
                value={approvalData.initial_credits}
                onChange={(e) => setApprovalData({ ...approvalData, initial_credits: parseInt(e.target.value) || 0 })}
                fullWidth
                margin="normal"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog(false)} disabled={processing}>
            Cancel
          </Button>
          <Button onClick={handleApprove} variant="contained" color="success" disabled={processing}>
            {processing ? 'Approving...' : 'Approve & Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={rejectDialog} onClose={() => !processing && setRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Registration</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Username:</strong> {selectedRequest.username}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Email:</strong> {selectedRequest.email}
              </Typography>

              <TextField
                label="Rejection Reason (optional)"
                multiline
                rows={3}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                fullWidth
                margin="normal"
                placeholder="Internal notes about why this request was rejected..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)} disabled={processing}>
            Cancel
          </Button>
          <Button onClick={handleReject} variant="contained" color="error" disabled={processing}>
            {processing ? 'Rejecting...' : 'Reject Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingRegistrations;
